var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import { until } from "@vueuse/core";
import Dexie from "dexie";
import { defineStore, storeToRefs } from "pinia";
import { computed, ref, watch } from "vue";
import { useHashedUserId } from "@/composables/hashedUserId";
import { assertDefined, ensureDefined } from "@/utils/assertions";
import { useUserStore } from "./userStore";
class UserTagStoreDatabase extends Dexie {
    constructor() {
        super("userTagStoreDatabase");
        Object.defineProperty(this, "tags", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: void 0
        });
        this.version(2).stores({ tags: "++id, userHash, lastUsed, tag" });
    }
}
const maxDbEntriesPerUser = 10000;
export function normalizeTag(tag) {
    return tag.replace(/^#/, "name:");
}
export const useUserTagsStore = defineStore("userTagsStore", () => {
    const { currentUser } = storeToRefs(useUserStore());
    const { hashedUserId } = useHashedUserId(currentUser);
    const db = new UserTagStoreDatabase();
    const tags = ref([]);
    const dbLoaded = ref(false);
    watch(() => hashedUserId.value, (userHash) => __awaiter(void 0, void 0, void 0, function* () {
        if (userHash) {
            tags.value = yield db.tags.where("userHash").equals(userHash).sortBy("lastUsed");
            if (tags.value.length > maxDbEntriesPerUser) {
                yield removeOldestEntries(tags.value.length - maxDbEntriesPerUser);
            }
            dbLoaded.value = true;
        }
    }), { immediate: true });
    /** removes the x oldest tags from the database */
    function removeOldestEntries(count) {
        return __awaiter(this, void 0, void 0, function* () {
            const oldestTags = tags.value.splice(0, count);
            yield db.tags.bulkDelete(oldestTags.map((o) => o.id));
        });
    }
    /** tags as string array */
    const userTags = computed(() => {
        return tags.value.map((o) => o.tag).reverse();
    });
    function onNewTagSeen(tag) {
        return __awaiter(this, void 0, void 0, function* () {
            yield until(dbLoaded).toBe(true);
            assertDefined(hashedUserId.value);
            tag = normalizeTag(tag);
            const tagSet = new Set(userTags.value);
            if (!tagSet.has(tag)) {
                const tagObject = {
                    tag,
                    userHash: hashedUserId.value,
                    lastUsed: Date.now(),
                };
                tags.value.push(tagObject);
                yield db.tags.add(tagObject);
            }
        });
    }
    function onMultipleNewTagsSeen(newTags) {
        return __awaiter(this, void 0, void 0, function* () {
            yield until(dbLoaded).toBe(true);
            const userHash = ensureDefined(hashedUserId.value);
            const tagSet = new Set(userTags.value);
            // only the ones that really are new
            const filteredNewTags = newTags.map(normalizeTag).filter((tag) => !tagSet.has(tag));
            if (filteredNewTags.length > 0) {
                const now = Date.now();
                const newTagObjects = filteredNewTags.map((tag) => ({
                    tag,
                    userHash,
                    lastUsed: now,
                }));
                tags.value = tags.value.concat(newTagObjects);
                yield db.tags.bulkAdd(newTagObjects);
            }
        });
    }
    function onTagUsed(tag) {
        return __awaiter(this, void 0, void 0, function* () {
            yield until(dbLoaded).toBe(true);
            tag = normalizeTag(tag);
            const dbTag = yield db.tags.get({ tag });
            if (dbTag) {
                yield db.tags.update(dbTag, { lastUsed: Date.now() });
            }
            else {
                const storedTag = tags.value.find((o) => o.tag === tag);
                const id = storedTag === null || storedTag === void 0 ? void 0 : storedTag.id;
                if (id !== undefined) {
                    yield db.tags.add(Object.assign(Object.assign({}, storedTag), { lastUsed: Date.now() }));
                }
            }
        });
    }
    return { userTags, onNewTagSeen, onTagUsed, onMultipleNewTagsSeen };
});
